import React from "react";
import PropTypes from "prop-types";
import Modal from "components/modal";
import { MdDelete, MdClose, MdCheck, MdWarning } from "react-icons/md";

/**
 * Modal de confirmation pour les actions de suppression
 * 
 * @param {Object} props - Les propriétés du composant
 * @param {boolean} props.isOpen - Indique si le modal est ouvert
 * @param {Function} props.onClose - Fonction appelée lors de la fermeture du modal
 * @param {Function} props.onConfirm - Fonction appelée lors de la confirmation de la suppression
 * @param {Object} props.item - L'élément à supprimer
 * @param {string} props.itemName - Le nom de l'élément à afficher
 * @param {string} [props.itemType="élément"] - Le type d'élément à supprimer (ex: "utilisateur", "rôle")
 * @param {string} [props.warning] - Message d'avertissement supplémentaire
 * @param {boolean} [props.loading=false] - État de chargement
 * @param {string} [props.title] - Titre personnalisé du modal (par défaut: "Supprimer {itemType}")
 * @param {string} [props.confirmLabel="Confirmer"] - Texte du bouton de confirmation
 * @param {string} [props.cancelLabel="Annuler"] - Texte du bouton d'annulation
 * @param {string} [props.primaryColor="#EF4444"] - Couleur principale pour les éléments d'UI (généralement rouge)
 * @param {string} [props.primaryColorHover="#DC2626"] - Couleur au survol
 */
const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  item, 
  itemName,
  itemType = "élément",
  warning,
  loading = false,
  title,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  primaryColor = "#EF4444", // Rouge par défaut
  primaryColorHover = "#DC2626"
}) => {
  const defaultTitle = `Supprimer ${itemType}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="sm">
      <div className="text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <MdDelete className="h-8 w-8 text-red-500" />
        </div>
        
        <h3 className="mb-3 text-xl font-semibold text-gray-800">
          {title || defaultTitle}
        </h3>
        
        <div className="mt-4 mb-6">
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer {itemType}{" "}
            <span className="font-bold text-gray-800">{itemName}</span>{" "}
            ?
          </p>
          
          <p className="mt-2 text-sm text-gray-500">
            Cette action est irréversible.
          </p>
          
          {warning && (
            <div className="mt-4 rounded-md bg-yellow-50 p-3 border border-yellow-100 flex items-start">
              <MdWarning className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-yellow-700 text-left">
                {warning}
              </p>
            </div>
          )}
        </div>
        
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            className="px-5 py-2 rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
            onClick={onClose}
            disabled={loading}
          >
            <span className="flex items-center gap-1.5">
              <MdClose className="h-4 w-4" />
              {cancelLabel}
            </span>
          </button>
          
          <button
            type="button"
            className="px-5 py-2 rounded-md text-white transition-colors"
            style={{
              backgroundColor: primaryColor,
              "--hover-bg": primaryColorHover
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = primaryColorHover}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = primaryColor}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center">
                <svg
                  className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Chargement...
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <MdCheck className="h-4 w-4" />
                {confirmLabel}
              </span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

DeleteConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  item: PropTypes.object,
  itemName: PropTypes.string.isRequired,
  itemType: PropTypes.string,
  warning: PropTypes.string,
  loading: PropTypes.bool,
  title: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  primaryColor: PropTypes.string,
  primaryColorHover: PropTypes.string,
};

export default DeleteConfirmationModal;